import React, { useState, useEffect } from "react";
import apiService from "../../ApiService";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; // Import separately
import { trackEvent } from "../../components/trackingUtils";
import SEOHead from "../../common/SEOHead";
import { baseUrl } from "../../config";

const TableIQ = () => {
  const [dbType, setDbType] = useState("mysql");
  const [server, setServer] = useState("");
  const [database, setDatabase] = useState("");
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [tables, setTables] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [metadata, setMetadata] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tableSearch, setTableSearch] = useState("");

  const handleFetchTables = async () => {
    if (!server || !database || !user) {
      setError("Please provide server, database, and user");
      return;
    }

    setLoading(true);
    setError(null);
    setTables([]);
    setSelectedTables([]);
    try {
      const payload = { db_type: dbType, server, database, user, password };
      const data = await apiService.fetchTableData("/list-tables", payload);
      if (data.error) throw new Error(data.error);
      setTables(data.tables || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTableSelect = (table) => {
    let updated = [...selectedTables];
    if (updated.includes(table)) updated = updated.filter((t) => t !== table);
    else if (updated.length < 2) updated.push(table);
    else {
      alert("You can select a maximum of 2 tables.");
      return;
    }
    setSelectedTables(updated);
  };

  const handleGenerateMetadata = async () => {
    if (selectedTables.length === 0) {
      setError("Please select at least one table");
      return;
    }
    trackEvent(
      "ClickGenerateDoc",
      JSON.stringify({ tables: selectedTables }),
      "TableIQ"
    );
    setLoading(true);
    setError(null);
    try {
      const payload = {
        db_type: dbType,
        server,
        database,
        user,
        password,
        table: selectedTables,
        domain: "General",
      };
      const data = await apiService.fetchTableData("/generate-doc", payload);
      if (data.error) throw new Error(data.error);
      setMetadata(data);
      trackEvent(
        "GenerateDocSuccess",
        JSON.stringify({
          tables: selectedTables,
          tableCount: Object.keys(data).length,
        }),
        "TableIQ"
      );
    } catch (err) {
      setError(err.message);
      trackEvent(
        "GenerateDocError",
        JSON.stringify({ error: err.message }),
        "TableIQ"
      );
    } finally {
      setLoading(false);
    }
  };

  // Download as Excel
  const downloadExcel = () => {
    if (!metadata || Object.keys(metadata).length === 0) return;
    trackEvent(
      "DownloadExcel",
      JSON.stringify({ tableCount: Object.keys(metadata).length }),
      "TableIQ"
    );
    const wb = XLSX.utils.book_new();
    Object.entries(metadata).forEach(([tableName, tableData]) => {
      const tableInfo = Object.values(tableData)[0] || {};
      const columns = tableInfo.columns || {};
      const data = Object.entries(columns).map(([colName, colData]) => ({
        Column: colName,
        Description: colData?.description || "",
        DataType: colData?.data_type || "",
        Nullable: colData?.nullable ? "Yes" : "No",
        Default: colData?.default_value || "",
        Constraints: colData?.constraints?.join(", ") || "",
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, tableName);
    });
    XLSX.writeFile(wb, "TableMetadata.xlsx");
  };

  // Download as PDF
  const downloadPDF = () => {
    if (!metadata || Object.keys(metadata).length === 0) return;
    trackEvent(
      "DownloadPDF",
      JSON.stringify({ tableCount: Object.keys(metadata).length }),
      "TableIQ"
    );
    const doc = new jsPDF();

    Object.entries(metadata).forEach(([tableName, tableData], index) => {
      const tableInfo = Object.values(tableData)[0] || {};
      const columns = tableInfo.columns || {};
      const data = Object.entries(columns).map(([colName, colData]) => [
        colName,
        colData?.description || "",
        colData?.data_type || "",
        colData?.nullable ? "Yes" : "No",
        colData?.constraints?.join(", ") || "",
      ]);

      if (index > 0) doc.addPage();

      doc.text(tableName, 14, 15);
      autoTable(doc, {
        head: [["Column", "Description", "Type", "Nullable", "Constraints"]],
        body: data,
        startY: 20,
      });
    });

    doc.save("TableMetadata.pdf");
  };

  useEffect(() => {
    trackEvent("PageLoad", "", "TableIQ");
  }, []);

  // Copy JSON to clipboard
  const copyToClipboard = () => {
    if (!metadata || Object.keys(metadata).length === 0) return;
    navigator.clipboard.writeText(JSON.stringify(metadata, null, 2));
    alert("Metadata copied to clipboard!");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEOHead
        title="TableIQ - Intelligent Table Metadata Generator"
        description="Generate comprehensive metadata documentation for your database tables with TableIQ. Supports MySQL, PostgreSQL, and SQL Server with Excel and PDF exports."
        keywords="Database metadata, Table documentation, TableIQ, MySQL metadata, PostgreSQL metadata, SQL Server metadata, Database analytics, Column details, Table description, Excel export, PDF export, Data modeling, Schema documentation, Database management, Intelligent metadata generation"
        canonical={baseUrl + "/tools/tableiq"}
      />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#0b1220] via-[#1a2332] to-[#0b1220] text-white py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-96 h-96 bg-[#E5C558]/10 rounded-full blur-3xl top-0 right-0 animate-pulse"></div>
          <div
            className="absolute w-96 h-96 bg-blue-500/10 rounded-full blur-3xl bottom-0 left-0 animate-pulse"
            style={{ animationDelay: "1s" }}
          ></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4">
          <div className="text-center">
            <div className="inline-flex items-center justify-center p-3 bg-[#E5C558]/20 rounded-2xl mb-6 backdrop-blur-sm">
              <svg
                className="w-10 h-10 text-[#E5C558]"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
              </svg>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Table
              </span>
              <span className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] bg-clip-text text-transparent">
                IQ
              </span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Intelligent database metadata generator with AI-powered
              documentation. Connect securely to MySQL, PostgreSQL, or SQL
              Server databases.
            </p>

            {/* Feature Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {[
                {
                  icon: "🔗",
                  title: "Secure Connect",
                  desc: "Encrypted connections",
                },
                {
                  icon: "📊",
                  title: "Smart Analysis",
                  desc: "AI-powered insights",
                },
                {
                  icon: "📁",
                  title: "Export Options",
                  desc: "Excel, PDF, JSON",
                },
                {
                  icon: "🚀",
                  title: "Fast Processing",
                  desc: "Real-time generation",
                },
              ].map((feature, idx) => (
                <div
                  key={idx}
                  className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10"
                >
                  <div className="text-2xl mb-2">{feature.icon}</div>
                  <h3 className="text-white font-semibold text-sm mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 text-xs">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Section - Light */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          {/* Security Disclaimer */}
          <div className="mb-8 bg-white shadow-lg border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#E5C558]/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-[#E5C558]"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                🔒 Your Security Matters
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { icon: "🚫", text: "We do not store your credentials" },
                { icon: "👁️", text: "Use read-only database user for safety" },
                { icon: "🔐", text: "All connections encrypted via HTTPS" },
                { icon: "📖", text: "Only metadata fetched, no modifications" },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 text-gray-700"
                >
                  <span className="text-lg">{item.icon}</span>
                  <span className="text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Database Connection Form */}
          <div className="bg-white shadow-lg border border-gray-200 rounded-2xl p-8 mb-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <div className="w-8 h-8 bg-[#E5C558]/20 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-[#E5C558]"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
                  </svg>
                </div>
                Database Connection
              </h2>
              <p className="text-gray-600">
                Connect to your database to analyze table metadata
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Database Type
                </label>
                <div className="relative">
                  <select
                    value={dbType}
                    onChange={(e) => setDbType(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-4 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#E5C558] focus:border-transparent transition-all appearance-none"
                  >
                    <option value="mysql">MySQL</option>
                    <option value="pgsql">PostgreSQL</option>
                    <option value="sqlsrv">SQL Server</option>
                  </select>
                  <svg
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Server
                </label>
                <input
                  type="text"
                  placeholder="localhost or IP address"
                  className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-4 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#E5C558] focus:border-transparent transition-all"
                  value={server}
                  onChange={(e) => setServer(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Database Name
                </label>
                <input
                  type="text"
                  placeholder="Enter database name"
                  className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-4 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#E5C558] focus:border-transparent transition-all"
                  value={database}
                  onChange={(e) => setDatabase(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Username
                </label>
                <input
                  type="text"
                  placeholder="Database username"
                  className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-4 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#E5C558] focus:border-transparent transition-all"
                  value={user}
                  onChange={(e) => setUser(e.target.value)}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  type="password"
                  placeholder="Database password (optional)"
                  className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-4 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#E5C558] focus:border-transparent transition-all"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-start mt-8">
              <button
                onClick={handleFetchTables}
                disabled={loading || !server || !database || !user}
                className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] hover:from-[#d4b54d] hover:to-[#E5C558] text-black font-semibold px-8 py-4 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 shadow-lg hover:shadow-[#E5C558]/25"
              >
                {loading ? (
                  <>
                    <svg
                      className="w-5 h-5 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                    Connecting...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Connect & Fetch Tables
                  </>
                )}
              </button>
            </div>
          </div>
          {error && (
            <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-3">
              <svg
                className="w-5 h-5 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              {error}
            </div>
          )}
          {/* Table Selection */}
          {tables.length > 0 && (
            <div className="bg-white shadow-lg border border-gray-200 rounded-2xl p-8 mb-8">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#E5C558]/20 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-[#E5C558]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  Select Tables
                  <span className="text-sm bg-[#E5C558]/20 text-[#E5C558] px-3 py-1 rounded-full font-normal">
                    Max 2
                  </span>
                </h2>
                <p className="text-gray-600">
                  Choose up to 2 tables for metadata analysis
                </p>
              </div>

              <div className="relative mb-6">
                <input
                  type="text"
                  placeholder="Search tables..."
                  className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 pl-12 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#E5C558] focus:border-transparent transition-all"
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value)}
                />
                <svg
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-80 overflow-y-auto border border-gray-200 rounded-xl p-4">
                {tables
                  .filter((table) =>
                    table.toLowerCase().includes(tableSearch.toLowerCase())
                  )
                  .map((table) => (
                    <button
                      key={table}
                      onClick={() => handleTableSelect(table)}
                      className={`p-4 rounded-xl border transition-all duration-200 text-left ${
                        selectedTables.includes(table)
                          ? "bg-gradient-to-r from-[#E5C558]/20 to-[#d4b54d]/10 border-[#E5C558]/50 text-[#E5C558]"
                          : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium truncate">{table}</span>
                        {selectedTables.includes(table) && (
                          <svg
                            className="w-5 h-5 text-[#E5C558] flex-shrink-0 ml-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    </button>
                  ))}
              </div>

              {selectedTables.length > 0 && (
                <div className="mt-6 flex items-center gap-3">
                  <div className="flex -space-x-1">
                    {selectedTables.map((table, idx) => (
                      <div
                        key={table}
                        className="bg-[#E5C558]/20 border-2 border-[#E5C558]/50 rounded-full px-3 py-1 text-sm text-[#E5C558] font-medium"
                      >
                        {table}
                      </div>
                    ))}
                  </div>
                  <span className="text-gray-600 text-sm">
                    Selected for analysis
                  </span>
                </div>
              )}
            </div>
          )}{" "}
          {/* Generate Documentation */}
          {selectedTables.length > 0 && (
            <div className="text-center mb-8">
              <button
                onClick={handleGenerateMetadata}
                disabled={loading}
                className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] hover:from-[#d4b54d] hover:to-[#E5C558] text-black font-bold px-12 py-4 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 mx-auto shadow-lg hover:shadow-[#E5C558]/25"
              >
                {loading ? (
                  <>
                    <svg
                      className="w-6 h-6 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                    Generating Documentation...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-6 h-6"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Generate AI Documentation
                  </>
                )}
              </button>
            </div>
          )}
          {/* Export Options */}
          {metadata && Object.keys(metadata).length > 0 && (
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 mb-8">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#E5C558]/20 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-[#E5C558]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  Export Documentation
                </h3>
                <p className="text-gray-400">
                  Download or copy your generated metadata
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={downloadExcel}
                  className="bg-green-600/20 border border-green-500/30 hover:bg-green-600/30 text-green-400 px-6 py-4 rounded-xl transition-all duration-200 flex items-center gap-3 justify-center"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="text-left">
                    <div className="font-semibold">Excel Export</div>
                    <div className="text-xs opacity-75">Spreadsheet format</div>
                  </div>
                </button>

                <button
                  onClick={downloadPDF}
                  className="bg-red-600/20 border border-red-500/30 hover:bg-red-600/30 text-red-400 px-6 py-4 rounded-xl transition-all duration-200 flex items-center gap-3 justify-center"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="text-left">
                    <div className="font-semibold">PDF Export</div>
                    <div className="text-xs opacity-75">Document format</div>
                  </div>
                </button>

                <button
                  onClick={copyToClipboard}
                  className="bg-gray-600/20 border border-gray-500/30 hover:bg-gray-600/30 text-gray-300 px-6 py-4 rounded-xl transition-all duration-200 flex items-center gap-3 justify-center"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                  </svg>
                  <div className="text-left">
                    <div className="font-semibold">Copy JSON</div>
                    <div className="text-xs opacity-75">Raw data format</div>
                  </div>
                </button>
              </div>
            </div>
          )}
          {/* Metadata Display */}
          {metadata && Object.keys(metadata).length > 0 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">
                  Generated Documentation
                </h3>
                <p className="text-gray-400">
                  AI-powered metadata analysis results
                </p>
              </div>

              {Object.entries(metadata).map(([tableName, tableData]) => {
                const tableInfo = Object.values(tableData)[0] || {};
                const columns = tableInfo.columns || {};
                const analytics = tableInfo.analytics || {};

                return (
                  <div
                    key={tableName}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8"
                  >
                    <div className="mb-6">
                      <h4 className="text-2xl font-bold text-white mb-3 flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#E5C558]/20 rounded-xl flex items-center justify-center">
                          <svg
                            className="w-6 h-6 text-[#E5C558]"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
                          </svg>
                        </div>
                        {tableName}
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                          <p className="text-gray-300 mb-2">
                            <span className="font-semibold text-white">
                              Description:
                            </span>
                          </p>
                          <p className="text-gray-200 bg-white/5 rounded-lg p-3 border border-white/10">
                            {tableInfo.table_description ||
                              "No description available"}
                          </p>
                        </div>

                        <div>
                          <p className="text-gray-300 mb-2">
                            <span className="font-semibold text-white">
                              Logical Group:
                            </span>
                          </p>
                          <p className="text-gray-200 bg-white/5 rounded-lg p-3 border border-white/10">
                            {tableInfo.logical_group || "Not specified"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Analytics Highlight */}
                    {analytics && Object.keys(analytics).length > 0 && (
                      <div className="mb-6 bg-gradient-to-r from-[#E5C558]/20 to-[#d4b54d]/10 border border-[#E5C558]/30 rounded-xl p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 bg-[#E5C558]/30 rounded-lg flex items-center justify-center">
                            <svg
                              className="w-5 h-5 text-[#E5C558]"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                            </svg>
                          </div>
                          <h5 className="text-lg font-bold text-[#E5C558]">
                            Table Analytics
                          </h5>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-white mb-1">
                              {analytics.row_count?.toLocaleString() || "N/A"}
                            </div>
                            <div className="text-sm text-gray-300">
                              Total Rows
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-white mb-1">
                              {Object.keys(columns).length}
                            </div>
                            <div className="text-sm text-gray-300">Columns</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-white mb-1">
                              {analytics.storage_size_mb || "N/A"} MB
                            </div>
                            <div className="text-sm text-gray-300">
                              Storage Size
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Columns */}
                    <div>
                      <h5 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-[#E5C558]"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Column Details
                      </h5>

                      {Object.keys(columns).length > 0 ? (
                        <div className="space-y-3">
                          {Object.entries(columns).map(([colName, colData]) => (
                            <div
                              key={colName}
                              className="bg-white/5 border border-white/10 rounded-xl p-4"
                            >
                              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                                <div className="lg:flex-1">
                                  <h6 className="font-bold text-[#E5C558] text-lg mb-2">
                                    {colName}
                                  </h6>
                                  <p className="text-gray-200 mb-2">
                                    {colData?.description ||
                                      "No description available"}
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    <span className="bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-sm border border-blue-500/30">
                                      {colData?.data_type || "Unknown"}
                                    </span>
                                    {colData?.nullable && (
                                      <span className="bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full text-sm border border-yellow-500/30">
                                        Nullable
                                      </span>
                                    )}
                                    {colData?.constraints?.map(
                                      (constraint, idx) => (
                                        <span
                                          key={idx}
                                          className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm border border-green-500/30"
                                        >
                                          {constraint}
                                        </span>
                                      )
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <div className="w-16 h-16 bg-gray-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg
                              className="w-8 h-8 text-gray-500"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <p className="text-gray-400">
                            No columns available for this table
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TableIQ;
